import * as React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { displayDate } from '@/lib/dates'
import { cn } from '@/lib/utils'

export interface DonationListRow {
  id: number
  gift_on: string
  donor: string
  donor_email?: string | null
  amount: string
  amount_cents?: number
  method: string
  payment_type?: string
  frequency_kind?: 'one_time' | 'recurring'
  campaign: string | null
  tribute_type?: 'honor' | 'memory' | null
  honoree_name?: string | null
  display_anonymous?: boolean
  donor_comment?: string | null
  status?: string
  refunded?: boolean
  tribute?: string | null
  receipt_email_send_id?: number | null
}

type SortKey = 'date' | 'amount' | 'donor'

const columns = (
  sort: (id: SortKey) => ListPageColumn<DonationListRow>['sort'],
): ListPageColumn<DonationListRow>[] => [
  { id: 'date', header: 'Date', sort: sort('date'), cell: (row) => displayDate(row.gift_on) },
  {
    id: 'donor',
    header: 'Donor',
    sort: sort('donor'),
    cell: (row) => (
      <div>
        <div className="flex items-center gap-1.5 font-medium">
          <span>{row.donor}</span>
          {row.display_anonymous && <Badge variant="neutral">Anonymous</Badge>}
        </div>
      </div>
    ),
  },
  {
    id: 'amount',
    header: 'Amount',
    sort: sort('amount'),
    headerClassName: 'text-right',
    className: 'text-right font-medium tabular-nums',
    cell: (row) => row.amount,
  },
  {
    id: 'campaign',
    header: 'Campaign',
    cell: (row) => (
      <span className={cn(row.campaign === null && 'text-muted-foreground')}>
        {row.campaign ?? 'General'}
      </span>
    ),
  },
  {
    id: 'frequency',
    header: 'Frequency',
    cell: (row) => (row.frequency_kind === 'recurring' ? 'Recurring' : 'One-time'),
  },
  { id: 'method', header: 'Payment type', cell: (row) => row.method },
  { id: 'tribute', header: 'Tribute', cell: (row) => tributeLabel(row) ?? '—' },
  {
    id: 'state',
    header: 'State',
    cell: (row) =>
      row.refunded ? (
        <Badge variant="neutral">Refunded</Badge>
      ) : row.status === 'Voided' ? (
        <Badge variant="destructive">Voided</Badge>
      ) : (
        'Settled'
      ),
  },
]

function matches(
  row: DonationListRow,
  search: string,
  filters: Readonly<Record<string, ReadonlySet<string>>>,
) {
  return matchesSearch(row, search) && matchesFilters(row, filters)
}

function matchesSearch(row: DonationListRow, search: string) {
  const query = search.trim().toLowerCase()
  return (
    !query ||
    `${row.donor} ${row.donor_email ?? ''} ${row.donor_comment ?? ''}`.toLowerCase().includes(query)
  )
}

function matchesFilters(
  row: DonationListRow,
  filters: Readonly<Record<string, ReadonlySet<string>>>,
) {
  const tribute =
    row.tribute_type === 'honor' ? 'In honor' : row.tribute_type === 'memory' ? 'In memory' : 'None'
  const values = [
    !filters.campaign.size || filters.campaign.has(row.campaign ?? 'General'),
    !filters.payment.size || filters.payment.has(row.payment_type ?? row.method),
    !filters.frequency.size ||
      filters.frequency.has(row.frequency_kind === 'recurring' ? 'Recurring' : 'One-time'),
    !filters.tribute.size || filters.tribute.has(tribute),
    !filters.anonymity.size || filters.anonymity.has(row.display_anonymous ? 'Anonymous' : 'Named'),
  ]
  return values.every(Boolean)
}

function visibleRows(
  rows: DonationListRow[],
  options: {
    search: string
    filters: Readonly<Record<string, ReadonlySet<string>>>
    startsOn: string
    endsOn: string
    sortKey: SortKey
    direction: 'ascending' | 'descending'
  },
) {
  const { search, filters, startsOn, endsOn, sortKey, direction } = options
  return rows
    .filter(
      (row) =>
        (!startsOn || row.gift_on >= startsOn) &&
        (!endsOn || row.gift_on <= endsOn) &&
        matches(row, search, filters),
    )
    .sort((left, right) => {
      const values =
        sortKey === 'amount'
          ? [left.amount_cents ?? 0, right.amount_cents ?? 0]
          : sortKey === 'donor'
            ? [left.donor, right.donor]
            : [left.gift_on, right.gift_on]
      const result = values[0] < values[1] ? -1 : values[0] > values[1] ? 1 : 0
      return direction === 'ascending' ? result : -result
    })
}

function tributeLabel(row: DonationListRow) {
  if (row.tribute) return row.tribute
  if (!row.tribute_type || !row.honoree_name) return null
  return `${row.tribute_type === 'honor' ? 'In honor of' : 'In memory of'} ${row.honoree_name}`
}

export function DonationsList({
  rows,
  loading,
  error,
  onRowOpen,
  onResendReceipt,
}: {
  rows: DonationListRow[]
  loading: boolean
  error: boolean
  onRowOpen?: (row: DonationListRow) => void
  onResendReceipt?: (emailSendId: number) => void
}) {
  const [search, setSearch] = React.useState('')
  const [startsOn, setStartsOn] = React.useState('')
  const [endsOn, setEndsOn] = React.useState('')
  const [sortKey, setSortKey] = React.useState<SortKey>('date')
  const [direction, setDirection] = React.useState<'ascending' | 'descending'>('descending')
  const [filters, setFilters] = React.useState({
    campaign: new Set<string>(),
    payment: new Set<string>(),
    frequency: new Set<string>(),
    tribute: new Set<string>(),
    anonymity: new Set<string>(),
  })
  const visible = React.useMemo(
    () => visibleRows(rows, { search, filters, startsOn, endsOn, sortKey, direction }),
    [direction, endsOn, filters, rows, search, sortKey, startsOn],
  )
  const toggleSort = (key: SortKey) =>
    sortKey === key
      ? setDirection((value) => (value === 'ascending' ? 'descending' : 'ascending'))
      : (setSortKey(key), setDirection('ascending'))
  const exportCsv = () => downloadDonationCsv(search, startsOn, endsOn, filters)
  return (
    <ListPage<DonationListRow>
      title="Donations"
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search donor, email, or comment…',
      }}
      toolbar={
        <>
          <DonationDateFilters
            startsOn={startsOn}
            endsOn={endsOn}
            onStartsOnChange={setStartsOn}
            onEndsOnChange={setEndsOn}
          />
          <FilterDropdown
            label="Campaign"
            emptyLabel="All campaigns"
            options={filterOptions(rows, 'campaign')}
            selected={filters.campaign}
            onChange={(value) => setFilters((current) => ({ ...current, campaign: value }))}
          />
          <FilterDropdown
            label="Payment"
            emptyLabel="All payment types"
            options={filterOptions(rows, 'payment')}
            selected={filters.payment}
            onChange={(value) => setFilters((current) => ({ ...current, payment: value }))}
          />
          <FilterDropdown
            label="Frequency"
            emptyLabel="All frequencies"
            options={['One-time', 'Recurring'].map((value) => ({ value, label: value }))}
            selected={filters.frequency}
            onChange={(value) => setFilters((current) => ({ ...current, frequency: value }))}
          />
          <FilterDropdown
            label="Tribute"
            emptyLabel="All tributes"
            options={['In honor', 'In memory', 'None'].map((value) => ({ value, label: value }))}
            selected={filters.tribute}
            onChange={(value) => setFilters((current) => ({ ...current, tribute: value }))}
          />
          <FilterDropdown
            label="Anonymity"
            emptyLabel="All donors"
            options={['Anonymous', 'Named'].map((value) => ({ value, label: value }))}
            selected={filters.anonymity}
            onChange={(value) => setFilters((current) => ({ ...current, anonymity: value }))}
          />
          <Button type="button" variant="outline" className="ml-auto h-8" onClick={exportCsv}>
            Export CSV
          </Button>
        </>
      }
      table={{
        rows: visible,
        columns: columns((key) => ({
          active: sortKey === key,
          direction,
          onToggle: () => toggleSort(key),
        })),
        getRowId: (row) => row.id,
        getRowLabel: (row) => `${row.donor} ${row.amount} Donation`,
        onRowOpen,
        actions: onResendReceipt
          ? (row) =>
              row.receipt_email_send_id
                ? [
                    {
                      label: 'Resend receipt',
                      onSelect: () => onResendReceipt(row.receipt_email_send_id!),
                    },
                  ]
                : []
          : undefined,
        state: loading ? 'loading' : error ? 'error' : 'ready',
        loadingTitle: 'Loading Donations',
        emptyTitle: 'No Donations yet',
        errorTitle: 'Donations unavailable',
        errorDescription: 'Refresh and try again.',
      }}
    />
  )
}

async function downloadDonationCsv(
  search: string,
  startsOn: string,
  endsOn: string,
  filters: Readonly<Record<string, ReadonlySet<string>>>,
) {
  const query = new URLSearchParams({ search, starts_on: startsOn, ends_on: endsOn })
  filters.campaign.forEach((value) => query.append('campaign', value))
  filters.payment.forEach((value) => query.append('payment_type', value))
  filters.frequency.forEach((value) => query.append('frequency', value))
  filters.tribute.forEach((value) => query.append('tribute', value))
  filters.anonymity.forEach((value) => query.append('anonymity', value))
  const response = await fetch(`/api/v1/donations/export?${query}`, { credentials: 'same-origin' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(await response.blob())
  link.download = 'donations.csv'
  link.click()
}

function filterOptions(rows: DonationListRow[], key: 'campaign' | 'payment') {
  return [
    ...new Set(
      rows.map((row) =>
        key === 'campaign' ? (row.campaign ?? 'General') : (row.payment_type ?? row.method),
      ),
    ),
  ].map((value) => ({ value, label: value }))
}

function DonationDateFilters({
  startsOn,
  endsOn,
  onStartsOnChange,
  onEndsOnChange,
}: {
  startsOn: string
  endsOn: string
  onStartsOnChange: (value: string) => void
  onEndsOnChange: (value: string) => void
}) {
  return (
    <>
      <Input
        type="date"
        aria-label="Donation date from"
        value={startsOn}
        onChange={(event) => onStartsOnChange(event.target.value)}
        className="h-8 w-auto text-caption"
      />
      <Input
        type="date"
        aria-label="Donation date through"
        value={endsOn}
        onChange={(event) => onEndsOnChange(event.target.value)}
        className="h-8 w-auto text-caption"
      />
    </>
  )
}
